import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { RotateCcw, Trash2, Loader2, Info } from "lucide-react";

const fmtDate = (s) => (s && s !== "-" ? new Date(s).toLocaleString("tr-TR") : "-");

export default function RecycleBin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/trash"); setRows(r.data); setSelected(new Set()); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (tid) => {
    try { await api.post(`/trash/${tid}/restore`); toast.success("Kayıt geri yüklendi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const purge = async (tid) => {
    if (!window.confirm("Bu kayıt KALICI olarak silinsin mi? Geri alınamaz.")) return;
    try { await api.delete(`/trash/${tid}`); toast.success("Kalıcı olarak silindi"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleRow = (tid) => setSelected((s) => {
    const n = new Set(s); n.has(tid) ? n.delete(tid) : n.add(tid); return n;
  });
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.trash_id)));

  const bulkPurge = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} kayıt KALICI olarak silinsin mi? Geri alınamaz.`)) return;
    try {
      const r = await api.post("/trash/bulk-purge", { ids });
      toast.success(`${r.data.deleted} kayıt kalıcı olarak silindi`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const purgeAll = async () => {
    if (rows.length === 0) return;
    if (!window.confirm(`Geri dönüşümdeki TÜM kayıtlar (${rows.length}) KALICI olarak silinsin mi? Geri alınamaz.`)) return;
    try {
      const r = await api.post("/trash/bulk-purge", { all: true });
      toast.success(`${r.data.deleted} kayıt kalıcı olarak silindi`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader title="Geri Dönüşüm" subtitle="Silinen kayıtlar burada tutulur" />
      <div className="p-6">
        <div className="flex items-center gap-2 bg-sky-50 border border-sky-200 text-sky-800 rounded-sm px-4 py-2.5 mb-4 text-sm" data-testid="retention-note">
          <Info size={16} /> Silinen kayıtlar <b className="mx-1">7 gün</b> saklanır, sonra otomatik silinir. İstediğiniz kaydı geri yükleyebilir veya kalıcı olarak silebilirsiniz.
        </div>

        <div className="flex items-center gap-3 mb-3">
          <button onClick={bulkPurge} disabled={selected.size === 0} data-testid="bulk-purge-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
            <Trash2 size={15} /> Seçilenleri Kalıcı Sil {selected.size > 0 ? `(${selected.size})` : ""}
          </button>
          <button onClick={purgeAll} disabled={rows.length === 0} data-testid="purge-all-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-semibold text-red-700 border border-red-300 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <Trash2 size={15} /> Tümünü Kalıcı Sil
          </button>
        </div>

        <div className="border border-zinc-200 bg-white rounded-sm overflow-auto" style={{ maxHeight: "calc(100vh - 260px)" }}>
          <table className="w-full data-table" data-testid="trash-table">
            <thead>
              <tr>
                <th className="w-10 text-center">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="trash-select-all" className="cursor-pointer" />
                </th>
                <th>Modül</th><th>Ürün / Ad</th><th>Müşteri</th><th>Tarih</th>
                <th className="text-right">Adet</th><th>Silen</th><th>Silinme Zamanı</th><th className="text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-10 text-zinc-400"><Loader2 className="animate-spin inline" size={18} /> Yükleniyor...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-zinc-400">Geri dönüşüm boş.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.trash_id} data-testid={`trash-row-${r.trash_id}`} className={selected.has(r.trash_id) ? "bg-amber-50" : ""}>
                  <td className="text-center">
                    <input type="checkbox" checked={selected.has(r.trash_id)} onChange={() => toggleRow(r.trash_id)}
                      data-testid={`trash-check-${r.trash_id}`} className="cursor-pointer" />
                  </td>
                  <td><span className="px-2 py-0.5 rounded-sm text-xs font-semibold bg-zinc-100 text-zinc-700">{r.modul}</span></td>
                  <td className="mono">{r.urun_kodu}</td>
                  <td>{r.musteri}</td>
                  <td className="mono">{r.tarih}</td>
                  <td className="num">{r.adet}</td>
                  <td className="text-xs text-zinc-500">{r.deleted_by}</td>
                  <td className="mono text-xs">{fmtDate(r.deleted_at)}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => restore(r.trash_id)} data-testid={`restore-${r.trash_id}`}
                        className="flex items-center gap-1 p-1.5 text-green-600 hover:bg-green-50 rounded-sm text-xs font-semibold"><RotateCcw size={14} /> Geri Yükle</button>
                      <button onClick={() => purge(r.trash_id)} data-testid={`purge-${r.trash_id}`}
                        className="flex items-center gap-1 p-1.5 text-red-600 hover:bg-red-50 rounded-sm text-xs font-semibold"><Trash2 size={14} /> Kalıcı Sil</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-xs text-zinc-400">{rows.length} kayıt · {selected.size} seçili</div>
      </div>
    </>
  );
}
